/**
 * Stale-chunk recovery.
 *
 * When the frontend is redeployed, the old content-hashed chunks are removed
 * from the server. A tab that was opened against the previous build still
 * references the old chunk names; lazy-loading a route then fails with
 * "Failed to fetch dynamically imported module". A full page reload pulls a
 * fresh index.html that points at the new chunk names, recovering the app.
 *
 * Used by both the global handlers in main.tsx and the route ErrorBoundary.
 */

const RELOAD_GUARD_KEY = 'chunk-reload-ts';
const RELOAD_GUARD_MS = 10_000; // at most one auto-reload per 10s — prevents reload loops

export function isChunkLoadError(message?: string | null): boolean {
  if (!message) return false;
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i.test(
    message
  );
}

/**
 * Force a one-time full reload to recover from a stale chunk reference.
 * Guarded so a genuinely broken deploy can't trap the user in a reload loop.
 * Returns true if a reload was triggered.
 */
export function reloadOnceForStaleChunks(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode etc.) — reload anyway, once is fine
  }
  window.location.reload();
  return true;
}
